/**
 * Amazon 案件のブランド照合提案 (再構成計画 §2.4・段階3)。brand_id が未設定で Amazon のブランド名があるとき、
 * GET /amazon/products/<asin>/brand-match の候補を「既存ブランド○○に紐づけますか」として出し、「紐づける」で
 * PATCH /amazon/products/<asin>/brand。自動では紐づけない。
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../services/apiClient';
import { useToast } from '../../../hooks/useToast';
import { getErrorMessage } from '../../../utils/getErrorMessage';

interface Candidate {
  brand_id: string;
  brand_name: string;
  confidence: 'high' | 'medium' | 'low';
  method: string;
  reason: string;
}

interface MatchResponse {
  status: 'linked' | 'matched' | 'candidate' | 'none';
  candidates: Candidate[];
  brand_name: string | null;
}

interface Props {
  asin: string;
  /** 案件の brand_id (あれば提案しない) */
  brandId?: string | null;
  brandName?: string | null;
}

export function AmazonBrandLinkPrompt({ asin, brandId, brandName }: Props) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const enabled = !!asin && !brandId && !!brandName?.trim();
  const { data } = useQuery({
    queryKey: ['amazon-brand-match', asin],
    queryFn: async () => (await apiClient.get<MatchResponse>(`/amazon/products/${asin}/brand-match`)).data,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
  const [choice, setChoice] = useState<string>('');
  const [dismissed, setDismissed] = useState(false);
  const link = useMutation({
    mutationFn: async (targetId: string) => (await apiClient.patch(`/amazon/products/${asin}/brand`, { brand_id: targetId })).data,
    onSuccess: () => {
      toast.success('ブランドに紐づけました');
      queryClient.invalidateQueries({ queryKey: ['product-detail', asin] });
      queryClient.invalidateQueries({ queryKey: ['amazon-brand-match', asin] });
      queryClient.invalidateQueries({ queryKey: ['brands'] });
    },
    onError: (err) => toast.error(`紐づけに失敗しました: ${getErrorMessage(err)}`),
  });

  if (!enabled || dismissed || !data || data.status === 'linked' || data.status === 'none' || data.candidates.length === 0) return null;
  const single = data.candidates.length === 1 ? data.candidates[0] : null;
  const selected = single?.brand_id ?? choice;

  return (
    <div className="amazon-brand-link-prompt" role="status" data-testid="amazon-brand-link-prompt">
      <span className="amazon-brand-link-text">
        {single
          ? <>既存のブランド「{single.brand_name}」に紐づけますか？<span className="amazon-brand-link-reason">（{single.reason}）</span></>
          : <>同じ名前のブランドが複数あります。紐づける先を選んでください</>}
      </span>
      {!single && (
        <select className="form-input amazon-brand-link-select" value={choice} onChange={(e) => setChoice(e.target.value)} aria-label="紐づけるブランド">
          <option value="">選択してください</option>
          {data.candidates.map((c) => <option key={c.brand_id} value={c.brand_id}>{c.brand_name}</option>)}
        </select>
      )}
      <span className="amazon-brand-link-actions">
        <button type="button" className="btn-modal btn-modal-sm" onClick={() => selected && link.mutate(selected)} disabled={!selected || link.isPending}>
          {link.isPending ? '紐づけ中...' : '紐づける'}
        </button>
        <button type="button" className="btn-modal-secondary btn-modal-secondary-sm" onClick={() => setDismissed(true)} disabled={link.isPending}>あとで</button>
      </span>
    </div>
  );
}
